/** IndexedDB：埋点热日志 + 日归档 zip（不再使用 localStorage） */

import { cloneForIdb } from '@/utils/analytics-log';

export const ANALYTICS_IDB_NAME = 'deepsage-analytics';
export const ANALYTICS_IDB_VERSION = 1;

const STORE_META = 'meta';
const STORE_LOGS = 'logs';
const STORE_ARCHIVES = 'archives';

/** @type {Promise<IDBDatabase> | null} */
let dbPromise = null;

function openDb() {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('indexedDB unavailable'));
  }
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(ANALYTICS_IDB_NAME, ANALYTICS_IDB_VERSION);
    req.onerror = () => reject(req.error || new Error('indexedDB open failed'));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(STORE_LOGS)) {
        const logs = db.createObjectStore(STORE_LOGS, { keyPath: 'id' });
        logs.createIndex('at', 'at', { unique: false });
        logs.createIndex('key', 'key', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_ARCHIVES)) {
        db.createObjectStore(STORE_ARCHIVES, { keyPath: 'day' });
      }
    };
  });
  return dbPromise;
}

/**
 * @param {string} storeName
 * @param {IDBTransactionMode} mode
 * @param {(store: IDBObjectStore) => unknown} run
 */
function withStore(storeName, mode, run) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, mode);
        const store = tx.objectStore(storeName);
        let result;
        try {
          result = run(store);
        } catch (err) {
          reject(err);
          return;
        }
        tx.oncomplete = () => {
          Promise.resolve(result).then(resolve, reject);
        };
        tx.onerror = () => reject(tx.error || new Error('indexedDB tx failed'));
        tx.onabort = () => reject(tx.error || new Error('indexedDB tx aborted'));
      })
  );
}

function reqToPromise(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('indexedDB request failed'));
  });
}

/** @param {string} key */
export async function idbGetMeta(key) {
  try {
    const row = await withStore(STORE_META, 'readonly', (store) => reqToPromise(store.get(key)));
    return row?.value;
  } catch {
    return undefined;
  }
}

/** @param {string} key @param {unknown} value */
export async function idbSetMeta(key, value) {
  await withStore(STORE_META, 'readwrite', (store) => {
    store.put({ key, value: cloneForIdb(value) });
  });
}

/** @param {Record<string, unknown>} map */
export async function idbSetMetaMap(map) {
  await withStore(STORE_META, 'readwrite', (store) => {
    Object.entries(map).forEach(([key, value]) => {
      store.put({ key, value: cloneForIdb(value) });
    });
  });
}

/** @returns {Promise<Record<string, unknown>>} */
export async function idbGetAllMeta() {
  try {
    const rows = await withStore(STORE_META, 'readonly', (store) => reqToPromise(store.getAll()));
    /** @type {Record<string, unknown>} */
    const out = {};
    (rows || []).forEach((row) => {
      if (row && typeof row.key === 'string') out[row.key] = row.value;
    });
    return out;
  } catch {
    return {};
  }
}

/**
 * @param {Array<Record<string, unknown>>} logs
 */
export async function idbPutLogs(logs) {
  if (!logs?.length) return;
  await withStore(STORE_LOGS, 'readwrite', (store) => {
    logs.forEach((row) => {
      if (!row || typeof row.id !== 'string') return;
      const plain = cloneForIdb(row);
      if (plain && typeof plain === 'object' && typeof plain.id === 'string') {
        store.put(plain);
      }
    });
  });
}

/** @param {string[]} ids */
export async function idbDeleteLogsByIds(ids) {
  if (!ids?.length) return;
  await withStore(STORE_LOGS, 'readwrite', (store) => {
    ids.forEach((id) => {
      if (id) store.delete(id);
    });
  });
}

/** @returns {Promise<Array<Record<string, unknown>>>} */
export async function idbGetAllLogs() {
  try {
    const rows = await withStore(STORE_LOGS, 'readonly', (store) => reqToPromise(store.getAll()));
    return Array.isArray(rows) ? rows : [];
  } catch {
    return [];
  }
}

/**
 * @param {number} minAt inclusive
 * @returns {Promise<Array<Record<string, unknown>>>}
 */
export async function idbGetLogsSince(minAt) {
  try {
    return await withStore(STORE_LOGS, 'readonly', (store) => {
      const index = store.index('at');
      const range = IDBKeyRange.lowerBound(minAt);
      return reqToPromise(index.getAll(range));
    });
  } catch {
    return [];
  }
}

/**
 * 同步热窗口内 durable 日志：写入当前集合，并删除同窗口内已不在集合中的行。
 * @param {Array<Record<string, unknown>>} durable
 * @param {number} minAt
 */
export async function idbSyncHotLogs(durable, minAt) {
  const existing = await idbGetLogsSince(minAt);
  const keep = new Set((durable || []).map((r) => r?.id).filter(Boolean));
  const toDelete = (existing || [])
    .filter((r) => r && r.key !== 'ui.click' && r.id && !keep.has(r.id))
    .map((r) => String(r.id));
  if (toDelete.length) await idbDeleteLogsByIds(toDelete);
  await idbPutLogs(durable);
}

/**
 * @param {number} maxAtExclusive
 * @returns {Promise<Array<Record<string, unknown>>>}
 */
export async function idbGetLogsBefore(maxAtExclusive) {
  try {
    return await withStore(STORE_LOGS, 'readonly', (store) => {
      const index = store.index('at');
      const range = IDBKeyRange.upperBound(maxAtExclusive, true);
      return reqToPromise(index.getAll(range));
    });
  } catch {
    return [];
  }
}

/**
 * @param {{
 *   day: string,
 *   blob: Blob,
 *   byteLength: number,
 *   logCount: number,
 *   schemaVersion: number,
 *   deviceId?: string,
 *   userId?: string,
 *   createdAt: number,
 * }} archive
 */
export async function idbPutArchive(archive) {
  await withStore(STORE_ARCHIVES, 'readwrite', (store) => {
    store.put(archive);
  });
}

/** @param {string} day */
export async function idbGetArchive(day) {
  try {
    return await withStore(STORE_ARCHIVES, 'readonly', (store) => reqToPromise(store.get(day)));
  } catch {
    return null;
  }
}

/** @returns {Promise<Array<Record<string, unknown>>>} */
export async function idbListArchives() {
  try {
    const rows = await withStore(STORE_ARCHIVES, 'readonly', (store) => reqToPromise(store.getAll()));
    return Array.isArray(rows) ? rows : [];
  } catch {
    return [];
  }
}

/** @param {string} day */
export async function idbDeleteArchive(day) {
  await withStore(STORE_ARCHIVES, 'readwrite', (store) => {
    store.delete(day);
  });
}

export async function idbClearAllLogs() {
  await withStore(STORE_LOGS, 'readwrite', (store) => {
    store.clear();
  });
}
